import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Tag, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { useContent } from "../context/ContentContext";

export default function PromoBanner() {
  const { content } = useContent();
  const navigate = useNavigate();
  const p = content?.promo_banner;
  const code = (p?.code || "").trim();
  const ref = useRef(null);
  const [copied, setCopied] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("ra_promo_dismissed") === (code || "on"); } catch { return false; }
  });
  const show = !!(p?.enabled && p?.text) && !dismissed;

  // Auto show/hide by optional start/end date window (inclusive, YYYY-MM-DD).
  const todayStr = new Date().toISOString().slice(0, 10);
  const inWindow = (!p?.start_date || todayStr >= p.start_date) && (!p?.end_date || todayStr <= p.end_date);
  const visible = show && inWindow;

  // Push the fixed navbar down by the banner's real height while visible.
  useEffect(() => {
    const root = document.documentElement;
    if (!visible) { root.style.setProperty("--promo-h", "0px"); return; }
    const apply = () => root.style.setProperty("--promo-h", `${ref.current?.offsetHeight || 0}px`);
    apply();
    const ro = new ResizeObserver(apply);
    if (ref.current) ro.observe(ref.current);
    window.addEventListener("resize", apply);
    return () => { ro.disconnect(); window.removeEventListener("resize", apply); root.style.setProperty("--promo-h", "0px"); };
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem("ra_promo_dismissed", code || "on"); } catch { /* ignore */ }
  };
  const copy = async () => {
    if (!code) return;
    try { await navigator.clipboard.writeText(code); setCopied(true); toast.success(`Code ${code} copied`); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };
  const book = () => navigate(code ? `/booking?promo=${encodeURIComponent(code)}` : "/booking");

  const daysLeft = p?.end_date ? Math.ceil((new Date(p.end_date + "T23:59:59") - new Date()) / 86400000) : null;

  return (
    <div ref={ref} data-testid="promo-banner" className="fixed top-0 inset-x-0 z-[60] bg-gradient-to-r from-rose to-[#7d3535] text-cream">
      <div className="container-lux py-2.5 pr-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-sm">
        <span className="inline-flex items-center gap-2 font-medium"><Tag size={15} className="text-gold shrink-0" /> {p.text}</span>
        {daysLeft != null && daysLeft >= 0 && (
          <span data-testid="promo-banner-countdown" className="text-cream/85 text-xs font-semibold uppercase tracking-wide">{daysLeft === 0 ? "Ends today" : `Ends in ${daysLeft} day${daysLeft > 1 ? "s" : ""}`}</span>
        )}
        {code && (
          <button onClick={copy} data-testid="promo-banner-code" className="inline-flex items-center gap-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors px-3 py-1 font-mono font-semibold tracking-wide">
            {code} {copied ? <Check size={13} /> : <Copy size={13} />}
          </button>
        )}
        <button onClick={book} data-testid="promo-banner-cta" className="rounded-full bg-gold text-forest font-semibold px-4 py-1 hover:bg-gold/90 transition-colors">
          {p.cta_label || "Book now"}
        </button>
      </div>
      <button onClick={dismiss} data-testid="promo-banner-dismiss" aria-label="Dismiss offer" className="absolute right-3 top-1/2 -translate-y-1/2 text-cream/70 hover:text-cream">
        <X size={16} />
      </button>
    </div>
  );
}
